import { formatDisplayEmail } from '@bs/core'
import { pantryColors } from '@pantry/ui'
import React from 'react'
import type { CouponCustomerResult } from '../hooks/useCouponCustomerSearch'

const p = pantryColors

const rowStyle: React.CSSProperties = {
  display: 'block',
  width: '100%',
  textAlign: 'left',
  padding: '8px 12px',
  fontSize: 13,
  color: p.gray[800],
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
}

const messageStyle: React.CSSProperties = {
  padding: '10px 12px',
  fontSize: 12,
  color: p.gray[500],
}

const setHover = (bg: string) => (e: React.MouseEvent<HTMLButtonElement>) => {
  e.currentTarget.style.background = bg
}

interface Props {
  visibleResults: CouponCustomerResult[]
  isLoading: boolean
  searchSupported: boolean
  canAddTyped: boolean
  query: string
  trimmed: string
  onAdd: (email: string) => void
}

/**
 * Absolute-positioned results panel for `CouponCustomerPicker`. Lists matching
 * customers (Cova / Greenline) and a synthetic "+ Add" row for a free-typed
 * email not in the results. Extracted from the picker to keep each file small.
 */
export const CouponCustomerSearchDropdown: React.FC<Props> = ({
  visibleResults,
  isLoading,
  searchSupported,
  canAddTyped,
  query,
  trimmed,
  onAdd,
}) => (
  <div
    style={{
      position: 'absolute',
      top: 'calc(100% - 2px)',
      left: 0,
      right: 0,
      marginTop: 4,
      maxHeight: 220,
      overflowY: 'auto',
      background: p.white,
      border: `1px solid ${p.gray[200]}`,
      borderRadius: 8,
      boxShadow: '0 4px 12px rgba(15, 23, 42, 0.08)',
      zIndex: 2,
    }}
  >
    {searchSupported && isLoading ? (
      <div style={messageStyle}>Searching…</div>
    ) : (
      <>
        {visibleResults.map((c) => (
          <button
            key={c.id}
            type="button"
            onClick={() => onAdd(c.email)}
            style={rowStyle}
            onMouseEnter={setHover(p.gray[50])}
            onMouseLeave={setHover('transparent')}
          >
            <span style={{ fontWeight: 600 }}>
              {c.name || formatDisplayEmail(c.email)}
            </span>
            {c.name ? (
              <span style={{ color: p.gray[500], marginLeft: 8 }}>
                {formatDisplayEmail(c.email)}
              </span>
            ) : null}
          </button>
        ))}
        {canAddTyped && (
          <button
            type="button"
            onClick={() => onAdd(trimmed)}
            style={{ ...rowStyle, color: p.brand.primary, fontWeight: 600 }}
            onMouseEnter={setHover(p.gray[50])}
            onMouseLeave={setHover('transparent')}
          >
            + Add "{trimmed}"
          </button>
        )}
        {searchSupported &&
          !isLoading &&
          visibleResults.length === 0 &&
          !canAddTyped && (
            <div style={messageStyle}>No customers match "{query}"</div>
          )}
      </>
    )}
  </div>
)
